import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity } from 'react-native';
import Modal from 'react-native-modal';

import { Box, Text } from '@components/Restyle';
import { Spinner } from '@components/shared/Spinner';
import theme from '@themes/theme';

const MODAL_MAX_WIDTH = 400;

/**
 * The native destructive-confirm surface (TA-10385).
 *
 * Native flows historically confirmed destructive actions with RN's
 * `Alert.alert`, an OS dialog that cannot host a spinner, cannot disable its
 * buttons, and dismisses the moment one is pressed. That makes three of the
 * global spinner rules unimplementable: "lock both confirm and cancel during
 * destructive operations", "keep modals open until the server confirms
 * success", and "restore the button and show an inline error on failure".
 *
 * This component is the in-app replacement. While `loading` is true it locks
 * every dismissal path — confirm, cancel, backdrop tap and the Android
 * hardware back button — and swaps the confirm label for an inline `Spinner`.
 *
 * Callers must `await` their mutation *before* closing, or the modal
 * disappears while the request is still in flight and the lock is pointless.
 *
 * `ConfirmModal` is NOT the native equivalent of this — despite its plain
 * `.tsx` filename it imports `WebModal.web` directly, so it is web-only.
 */
interface NativeConfirmModalProps {
  visible: boolean;
  /** Dismiss without acting. Ignored while `loading`. */
  onCancel: () => void;
  /** Perform the action. Should `await` before the caller closes the modal. */
  onConfirm: () => void;
  title: string;
  message?: string;
  /**
   * Rendered in bold immediately before `message` — for naming the subject of
   * the action (a member, a project) without concatenating it into the
   * translated sentence.
   */
  emphasis?: string;
  bulletItems?: string[];
  cancelLabel?: string;
  confirmLabel?: string;
  /**
   * Renders the confirm label in `alertRed` for irreversible actions (delete,
   * archive). Defaults to the brand green used by additive confirmations.
   */
  destructive?: boolean;
  /**
   * Locks confirm, cancel, the backdrop and the hardware back button, and
   * replaces the confirm label with a spinner.
   */
  loading?: boolean;
  testID?: string;
}

export const NativeConfirmModal: React.FC<NativeConfirmModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  title,
  message,
  emphasis,
  bulletItems,
  cancelLabel,
  confirmLabel,
  destructive = false,
  loading = false,
  testID = 'native-confirm-modal',
}) => {
  const { t } = useTranslation();
  const confirmColor = destructive ? 'alertRed' : 'greenSecondary';

  /**
   * `disabled={loading}` cannot stop a double-tap on its own: `loading` only
   * reaches the button on the *next* commit, and React Native hands queued
   * touches to JS in one batch when the thread is janked, so both taps run
   * before it (TA-10386 QA finding).
   *
   * The press bumps `confirmTick` purely to guarantee a commit, and the guard
   * is released in that commit's effect. Tying the release to a render of our
   * own — rather than to `loading` going true, or to the dialog closing —
   * keeps the guard's lifetime to exactly the window `disabled` cannot cover,
   * and means a caller that reports no loading state, or that leaves this
   * dialog open after a failed request, still gets a working button back
   * instead of a permanently dead one.
   */
  const confirmDispatched = useRef(false);
  const [confirmTick, setConfirmTick] = useState(0);
  useEffect(() => {
    confirmDispatched.current = false;
  }, [confirmTick]);

  const handleConfirm = () => {
    if (loading || confirmDispatched.current) return;
    confirmDispatched.current = true;
    setConfirmTick((tick) => tick + 1);
    onConfirm();
  };

  /**
   * The same window applies to every dismissal path, and closing one of those
   * mid-flight is worse than a double submit: the dialog goes away as if the
   * user had backed out, while the destructive mutation carries on. So Cancel,
   * the backdrop and hardware back are all dead once Confirm has been
   * dispatched, not just once `loading` has landed.
   */
  const handleCancel = () => {
    if (loading || confirmDispatched.current) return;
    onCancel();
  };

  return (
    <Modal
      testID={testID}
      isVisible={visible}
      // Both dismissal paths bypass the buttons' own `disabled`, so they have
      // to be neutralised separately while the mutation is in flight.
      onBackdropPress={loading ? undefined : handleCancel}
      onBackButtonPress={loading ? undefined : handleCancel}
      backdropOpacity={0.5}
      animationIn='fadeIn'
      animationOut='fadeOut'
      style={styles.modal}>
      {/* Restyle props rather than a module-scope StyleSheet: reading
          `theme.colors`/`theme.spacing` at import time crashes any suite that
          mocks the theme, at the moment this file is required (TA-10386). */}
      <Box
        backgroundColor='white'
        borderRadius='m'
        paddingVertical='m'
        paddingHorizontal='l'
        width='90%'
        maxWidth={MODAL_MAX_WIDTH}>
        <Text variant='heading2' color='textPrimary' marginBottom='xs'>
          {title}
        </Text>

        {(!!message || !!emphasis) && (
          <Text variant='body' color='textSecondary'>
            {!!emphasis && (
              <Text variant='body' color='textSecondary' fontWeight='bold'>
                {emphasis}
              </Text>
            )}
            {!!emphasis && !!message && ' '}
            {message}
          </Text>
        )}

        {!!bulletItems?.length && (
          <Box marginTop='xs'>
            {bulletItems.map((item, index) => (
              <Box key={index} flexDirection='row'>
                <Text variant='body' color='textSecondary'>
                  {'•  '}
                </Text>
                <Box flex={1}>
                  <Text variant='body' color='textSecondary'>
                    {item}
                  </Text>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box
          flexDirection='row'
          justifyContent='flex-end'
          alignItems='center'
          paddingVertical='m'>
          <TouchableOpacity
            onPress={handleCancel}
            disabled={loading}
            accessibilityRole='button'
            accessibilityState={{ disabled: loading }}
            accessibilityLabel={cancelLabel || t('shared:cancel')}>
            <Text
              variant='buttonLabel'
              color={loading ? 'grey04' : 'textSecondary'}>
              {cancelLabel || t('shared:cancel')}
            </Text>
          </TouchableOpacity>

          <Box width={40} />

          <TouchableOpacity
            onPress={handleConfirm}
            disabled={loading}
            accessibilityRole='button'
            accessibilityState={{ disabled: loading }}
            accessibilityLabel={confirmLabel || t('shared:confirm')}>
            {loading ? (
              <Spinner
                size={16}
                strokeWidth={2}
                color={theme.colors[confirmColor]}
              />
            ) : (
              <Text variant='buttonLabel' color={confirmColor}>
                {confirmLabel || t('shared:confirm')}
              </Text>
            )}
          </TouchableOpacity>
        </Box>
      </Box>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0,
  },
});
